import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { CustomService } from '../../../services/api/custom/CustomService';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { Select } from 'antd';

import './styles.scss';

function RegisterProductForm({ id }) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue, // Usado para atualizar valores dos inputs
    formState: { errors },
  } = useForm();

  const { tipo } = useParams();
  const navigate = useNavigate();
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [segmentosOptions, setSegmentosOptions] = useState([]);
  const [selectedSegmentos, setSelectedSegmentos] = useState([]);

  useEffect(() => {
    CustomService.getRequest('/interno/categorias')
      .then((resp) => setCategories(resp || []))
      .catch(() => toast.error('Não foi possível carregar categorias'));
  }, []);

  useEffect(() => {
    CustomService.getRequest('/interno/segmentos')
      .then((resp) => setSegmentosOptions(resp || []))
      .catch(() => toast.error('Não foi possível carregar segmentos'));
  }, []);

  const handleSegmentosChange = (values) => {
    setSelectedSegmentos((prev) => {
      const prevMap = new Map(
        (prev || []).map((seg) => [String(seg.segmento_id), seg])
      );
      return (values || []).map((value) => {
        const existing = prevMap.get(String(value));
        if (existing) return existing;
        const fromList = (segmentosOptions || []).find(
          (seg) => String(seg.id) === String(value)
        );
        return {
          segmento_id: Number(value),
          prioridade: 0,
          segmento_nome: fromList?.nome || '',
        };
      });
    });
  };

  const updateSegmentoPrioridade = (segmentoId, prioridade) => {
    setSelectedSegmentos((prev) =>
      (prev || []).map((seg) =>
        Number(seg.segmento_id) === Number(segmentoId)
          ? { ...seg, prioridade }
          : seg
      )
    );
  };

  const onSubmit = async (data) => {
    // Converter todas as strings de preço para inteiro (cents)
    const toInteger = (strValue) =>
      parseInt(String(strValue).replace(/[^\d]/g, ''), 10) || 0;

    const estoqueInt = Number.isFinite(Number(data.estoque))
      ? Math.max(0, parseInt(String(data.estoque), 10) || 0)
      : 0;

    const segmentosPayload = (selectedSegmentos || []).map((seg) => ({
      segmento_id: Number(seg.segmento_id),
      prioridade: Math.max(0, parseInt(String(seg.prioridade), 10) || 0),
    }));

    if (segmentosPayload.some((seg) => !Number.isFinite(seg.segmento_id) || seg.segmento_id <= 0)) {
      toast.warn('Selecione segmentos válidos.');
      return;
    }

    // Nova rota de cadastro
    if (!id) {
      try {
        const formattedData = {
          ...data,
          preco_custo: toInteger(data.preco_custo),
          preco: toInteger(data.preco),
          preco_b: toInteger(data.preco_b),
          preco_c: toInteger(data.preco_c),
        };

        const formData = new FormData();
        formData.append('nome', formattedData.nome);
        formData.append('fid', formattedData.fid);
        formData.append('tipo', formattedData.tipo);
        formData.append('preco_custo', formattedData.preco_custo);
        formData.append('preco', formattedData.preco);
        formData.append('preco_b', formattedData.preco_b);
        formData.append('preco_c', formattedData.preco_c);
        formData.append('em_destaque', data.em_destaque ? 'true' : 'false');
        formData.append('estoque', String(estoqueInt));
        formData.append('segmentos', JSON.stringify(segmentosPayload));
        selectedCategories?.forEach((catId) =>
          formData.append('categorias', catId)
        );
        if (imageFile) {
          formData.append('imagem', imageFile);
        }

        await CustomService.postRequest('/interno/produto', formData);
        toast.success('Produto cadastrado com sucesso!');
        let redirectTo = formattedData.tipo;
        reset();
        setImageFile(null);
        setExistingImageUrl(null);
        navigate(`/${redirectTo}`);
      } catch (error) {
        console.error('Erro ao cadastrar produto:', error);
        toast.error('Erro ao cadastrar produto.');
      }
    }
    // Edição de produto
    else {
      // Garantir que não haja NaN
      if (!data.preco_custo) {
        data.preco_custo = '0.00';
      }
      const formattedData = {
        ...data,
        preco_custo: toInteger(data.preco_custo),
        preco: toInteger(data.preco),
        preco_b: toInteger(data.preco_b),
        preco_c: toInteger(data.preco_c),
      };

      const formData = new FormData();
      formData.append('id', id);
      formData.append('nome', formattedData.nome);
      formData.append('tipo', formattedData.tipo);
      formData.append('preco_custo', formattedData.preco_custo);
      formData.append('preco', formattedData.preco);
      formData.append('preco_b', formattedData.preco_b);
      formData.append('preco_c', formattedData.preco_c);
      formData.append('em_destaque', data.em_destaque ? 'true' : 'false');
      formData.append('estoque', String(estoqueInt));
      formData.append('segmentos', JSON.stringify(segmentosPayload));
      selectedCategories?.forEach((catId) =>
        formData.append('categorias[]', catId)
      );
      if (existingImageUrl) {
        formData.append('imagemUrl', existingImageUrl);
      } else if (imageFile) {
        formData.append('imagem', imageFile);
      } else {
        formData.append('deletarImagem', true);
      }

      await CustomService.patchRequest(`/interno/produto`, formData);
      toast.success('Produto atualizado com sucesso!');
      navigate(`/${formattedData.tipo}`);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
  };

  const handleDeleteImage = () => {
    setExistingImageUrl(null);
  };

  const productSingleView = async (pid) => {
    try {
      const resp = await CustomService.getRequest(`/interno/produto/${pid}`);
      // Supondo que o back-end retorne preco_custo (em cents)
      const costCents = resp.preco_custo || 0;
      const costFormatted = (costCents / 100).toFixed(2);

      setValue('nome', resp.nome);
      setValue('fid', resp.fid);
      setValue('tipo', resp.tipo);
      setValue('preco_custo', costFormatted);
      setValue('em_destaque', !!resp.em_destaque);
      setValue('estoque', Number.isFinite(Number(resp.estoque)) ? String(resp.estoque) : '0');

      // Preço A: usar valor do backend (preco_produto) ou calcular
      if (resp.preco_produto !== undefined && resp.preco_produto !== null) {
        setValue('preco', (resp.preco_produto / 100).toFixed(2));
      } else {
        const precoCents = Math.round(costCents * 1.4);
        setValue('preco', (precoCents / 100).toFixed(2));
      }

      // Preço B: usar valor do backend (preco_produto_b) ou calcular
      if (resp.preco_produto_b !== undefined && resp.preco_produto_b !== null) {
        setValue('preco_b', (resp.preco_produto_b / 100).toFixed(2));
      } else {
        const precoB_Cents = Math.round(costCents * 1.5);
        setValue('preco_b', (precoB_Cents / 100).toFixed(2));
      }

      // Preço C: usar valor do backend (preco_produto_c) ou calcular
      if (resp.preco_produto_c !== undefined && resp.preco_produto_c !== null) {
        setValue('preco_c', (resp.preco_produto_c / 100).toFixed(2));
      } else {
        const precoC_Cents = Math.round(costCents * 1.6);
        setValue('preco_c', (precoC_Cents / 100).toFixed(2));
      }

      setSelectedCategories(
        resp.categorias?.map((cat) => String(cat.id)) || []
      );

      setSelectedSegmentos(
        resp.segmentos?.map((seg) => ({
          segmento_id: Number(seg.segmento_id),
          prioridade: Number.isFinite(Number(seg.prioridade))
            ? Number(seg.prioridade)
            : 0,
          segmento_nome: seg.segmento_nome || '',
        })) || []
      );

      setExistingImageUrl(resp.imagem);
    } catch (error) {
      console.error('Erro ao buscar produto:', error);
    }
  };

  useEffect(() => {
    if (id) {
      productSingleView(id);
    }
  }, [id, setValue]);

  return (
    <div className="flex flex-col w-full gap-3">
     <form
  onSubmit={handleSubmit(onSubmit)}
  className="flex flex-wrap gap-3 w-full"
>
  {/* Primeira linha: Nome + Em Destaque */}
  <div className="w-full md:w-[70%]">
    <label className="block mb-1 font-medium">Nome:</label>
    <input
      type="text"
      className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
      {...register('nome', { required: 'O nome é obrigatório' })}
    />
    {errors.nome && (
      <p className="text-red-500 text-sm">{errors.nome.message}</p>
    )}
  </div>

  <div className="w-full md:w-[28%] flex items-center mt-6">
    <Controller
      name="em_destaque"
      control={control}
      defaultValue={false}
      render={({ field }) => (
        <input
          type="checkbox"
          checked={field.value}
          onChange={(e) => field.onChange(e.target.checked)}
          className="w-5 h-5 accent-blue-500"
        />
      )}
    />
    <label className="ml-2 cursor-pointer select-none text-sm md:text-base">
      <span className="font-medium">Em Destaque</span>
      <span className="text-gray-500 text-xs ml-1">
        (Destaca na página da loja)
      </span>
    </label>
  </div>

  {/* Fid */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Fid:</label>
    <input
      type="text"
      className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
      {...register('fid', { required: 'O Fid é obrigatório' })}
      readOnly={!!id}
      disabled={!!id}
    />
    {errors.fid && (
      <p className="text-red-500 text-sm">{errors.fid.message}</p>
    )}
  </div>

  {/* Tipo */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Tipo:</label>
    <select
      defaultValue={tipo || 'diversos'}
      className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
      {...register('tipo', { required: 'O tipo é obrigatório' })}
    >
      <option value="expediente">Expediente</option>
      <option value="limpeza">Limpeza</option>
      <option value="diversos">Diversos</option>
    </select>
    {errors.tipo && (
      <p className="text-red-500 text-sm">{errors.tipo.message}</p>
    )}
  </div>

  {/* Custo */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Custo:</label>
    <Controller
      name="preco_custo"
      control={control}
      rules={{ required: 'O custo é obrigatório' }}
      render={({ field }) => (
        <NumericFormat
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
          thousandSeparator="."
          decimalSeparator=","
          prefix="R$ "
          decimalScale={2}
          fixedDecimalScale={true}
          allowNegative={false}
          value={field.value}
          onValueChange={(values) => {
            field.onChange(values.value);
            const costFloat = parseFloat(values.value);
            if (!isNaN(costFloat)) {
              const costCents = Math.round(costFloat * 100);
              const precoCents = Math.round(costCents * 1.4);
              const precoB_Cents = Math.round(costCents * 1.5);
              const precoC_Cents = Math.round(costCents * 1.6);

              setValue('preco', (precoCents / 100).toFixed(2));
              setValue('preco_b', (precoB_Cents / 100).toFixed(2));
              setValue('preco_c', (precoC_Cents / 100).toFixed(2));
            } else {
              setValue('preco', '');
              setValue('preco_b', '');
              setValue('preco_c', '');
            }
          }}
        />
      )}
    />
    {errors.preco_custo && (
      <p className="text-red-500 text-sm">{errors.preco_custo.message}</p>
    )}
  </div>

  {/* Preço A */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Preço A:</label>
    <Controller
      name="preco"
      control={control}
      render={({ field }) => (
        <NumericFormat
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
          thousandSeparator="."
          decimalSeparator=","
          prefix="R$ "
          decimalScale={2}
          fixedDecimalScale={true}
          allowNegative={false}
          value={field.value}
          onValueChange={(values) => field.onChange(values.value)}
        />
      )}
    />
    {errors.preco && (
      <p className="text-red-500 text-sm">{errors.preco.message}</p>
    )}
  </div>

  {/* Preço B */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Preço B:</label>
    <Controller
      name="preco_b"
      control={control}
      render={({ field }) => (
        <NumericFormat
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
          thousandSeparator="."
          decimalSeparator=","
          prefix="R$ "
          decimalScale={2}
          fixedDecimalScale={true}
          allowNegative={false}
          value={field.value}
          onValueChange={(values) => field.onChange(values.value)}
        />
      )}
    />
    {errors.preco_b && (
      <p className="text-red-500 text-sm">{errors.preco_b.message}</p>
    )}
  </div>

  {/* Preço C */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Preço C:</label>
    <Controller
      name="preco_c"
      control={control}
      render={({ field }) => (
        <NumericFormat
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
          thousandSeparator="."
          decimalSeparator=","
          prefix="R$ "
          decimalScale={2}
          fixedDecimalScale={true}
          allowNegative={false}
          value={field.value}
          onValueChange={(values) => field.onChange(values.value)}
        />
      )}
    />
    {errors.preco_c && (
      <p className="text-red-500 text-sm">{errors.preco_c.message}</p>
    )}
  </div>

  {/* Estoque */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Estoque:</label>
    <input
      type="number"
      min={0}
      step={1}
      className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
      {...register('estoque', {
        required: 'O estoque é obrigatório',
        min: { value: 0, message: 'O estoque não pode ser negativo' },
        pattern: {
          value: /^\d+$/g,
          message: 'Informe um número inteiro válido',
        },
      })}
    />
    {errors.estoque && (
      <p className="text-red-500 text-sm">{errors.estoque.message}</p>
    )}
  </div>

  {/* Categorias */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Categorias:</label>
    <Select
      mode="multiple"
      allowClear
      style={{ width: '100%' }}
      placeholder="Selecione as categorias"
      value={selectedCategories}
      onChange={setSelectedCategories}
      optionFilterProp="label"
      optionLabelProp="label"
      showSearch
    >
      {categories.map((cat) => (
        <Select.Option key={cat.id} value={String(cat.id)} label={cat.nome}>
          {cat.nome}
        </Select.Option>
      ))}
    </Select>
  </div>

  {/* Segmentos */}
  <div className="w-full md:w-[48.9%]">
    <label className="block mb-1 font-medium">Segmentos:</label>
    <Select
      mode="multiple"
      allowClear
      style={{ width: '100%' }}
      placeholder="Selecione os segmentos"
      value={(selectedSegmentos || []).map((seg) => String(seg.segmento_id))}
      onChange={handleSegmentosChange}
      optionFilterProp="label"
      optionLabelProp="label"
      showSearch
    >
      {(segmentosOptions || []).map((seg) => (
        <Select.Option key={seg.id} value={String(seg.id)} label={seg.nome}>
          {seg.nome}
        </Select.Option>
      ))}
    </Select>
    <span className="text-xs text-gray-500">
      Menor prioridade = mais importante.
    </span>
  </div>

  {(selectedSegmentos || []).length > 0 && (
    <div className="w-full">
      <div className="border border-gray-200 rounded-md p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(selectedSegmentos || []).map((seg) => (
            <div
              key={seg.segmento_id}
              className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-3 py-2"
            >
              <span className="text-sm font-medium">
                {seg.segmento_nome || `ID ${seg.segmento_id}`}
              </span>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Prioridade</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={seg.prioridade}
                  onChange={(e) =>
                    updateSegmentoPrioridade(seg.segmento_id, e.target.value)
                  }
                  className="w-[80px] border border-gray-300 rounded-md p-1 text-sm text-center"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )}

  {/* Imagem atual */}
  {existingImageUrl && (
    <div className="w-full md:w-[48.9%] flex flex-col gap-2">
      <label className="block mb-1 font-medium">Imagem Atual:</label>
      <img
        src={existingImageUrl}
        alt="Imagem do produto"
        className="w-40 h-40 object-contain cursor-pointer shadow-sm hover:scale-105 transition-transform"
        onClick={() => window.open(existingImageUrl, '_blank')}
      />
      <button
        type="button"
        onClick={handleDeleteImage}
        className="bg-red-500 text-white p-2 rounded-md w-[160px] text-sm hover:bg-red-600 transition"
      >
        Excluir Imagem
      </button>
    </div>
  )}

  {/* Upload de imagem */}
  {!existingImageUrl && (
    <div className="w-full md:w-[48.9%]">
      <label className="block mb-1 font-medium">Imagem:</label>
      <input
        type="file"
        accept="image/*"
        className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
        onChange={handleImageChange}
      />
    </div>
  )}

  {/* Botão Submit */}
  <div className="w-full">
    <input
      type="submit"
      value={id ? 'Editar' : 'Cadastrar'}
      className="bg-blue-default text-white rounded-md p-2 mt-4 cursor-pointer text-sm hover:bg-blue-600 transition"
    />
  </div>
</form>
    </div>
  );
}

export default RegisterProductForm;
